import api from '@/utils/httpClient';
import { ApiListResponse } from '@oncolens/oncolens-ui/lib/dto/api/ApiResponse';
import { AxiosError } from 'axios';
import { useMutation, UseMutationOptions, useQuery } from 'react-query';
import { TreatmentHistoryDto, TreatmentHistoryForCreationDto, TreatmentHistoryForManipulationDto } from '../dtos/TreatmentHistory';
import { treatmentHistoryKeys } from '../TreatmentHistory/treatmentHistory.keys';

async function getTreatmentHistory(patientId: string) {
  const config: any = { containsPHI: false }
  return api.get(`/api/treatmenthistories?filters=patientId==${patientId}&sortorder=sortorder`, config)
    .then(response => {
      const parsedResponse = response.data as ApiListResponse<TreatmentHistoryDto>;

      return parsedResponse.data;
    });
}

function useGetTreatmentHistory(patientId: string) {
  return useQuery(
    treatmentHistoryKeys.list(patientId),
    async () => getTreatmentHistory(patientId),
  );
}

async function createTreatmentHistory(patientId: string, data: TreatmentHistoryForCreationDto[]) {
  const config: any = { containsPHI: false }
  return api.post(`/api/treatmenthistories?patientId=${patientId}`, data, config)
    .then(response => {
      const parsedResponse = response.data as ApiListResponse<TreatmentHistoryDto>;

      return parsedResponse.data;
    });
}

interface CreateTreatmentHistoryItemParameters {
  id: string;
  data: TreatmentHistoryForCreationDto;
}

export interface CreateTreatmentHistoryItemsParameters {
  id: string;
  data: TreatmentHistoryForCreationDto[];
}

function useCreateTreatmentHistoryItem(options?: UseMutationOptions<TreatmentHistoryDto[], AxiosError, CreateTreatmentHistoryItemParameters>) {
  return useMutation(
    async ({ id: patientId, data }) => createTreatmentHistory(patientId, [data]),
    options,
  );
}

function useCreateTreatmentHistoryItems(options?: UseMutationOptions<TreatmentHistoryDto[], AxiosError, CreateTreatmentHistoryItemsParameters>) {
  return useMutation(
    async ({ id: patientId, data }) => createTreatmentHistory(patientId, data),
    options,
  );
}

async function updateTreatmentHistoryItem(id: string, data: TreatmentHistoryForManipulationDto) {
  const config: any = { containsPHI: false }
  return api.put(`/api/treatmenthistories/${id}`, data, config)
    .then(response => {});
}

interface UpdateTreatmentHistoryParameters {
  id: string;
  data: TreatmentHistoryForManipulationDto;
}

function useUpdateTreatmentHistoryItem(options?: UseMutationOptions<void, AxiosError, UpdateTreatmentHistoryParameters>) {
  return useMutation(
    async ({ id, data }) => updateTreatmentHistoryItem(id, data),
    options,
  );
}

async function updateTreatmentHistoriesSortOrder(patientId: string, itemsToUpdate: UpdateTreatmentHistorySortOrderParameters[]) {
  const config: any = { containsPHI: false }
  return api.put(`/api/treatmenthistories/sortorder?patientId=${patientId}`, itemsToUpdate, config)
    .then(response => {});
}

interface UpdateTreatmentHistorySortOrderParameters {
  id: string;
  sortOrder: number;
}

function useUpdateTreatmentHistoriesSortOrder(options?: UseMutationOptions<void, AxiosError, {patientId: string, itemsToUpdate: UpdateTreatmentHistorySortOrderParameters[]}>) {
  return useMutation(
    async ({patientId, itemsToUpdate}) => updateTreatmentHistoriesSortOrder(patientId, itemsToUpdate),
    options,
  );
}

async function deleteTreatmentHistoryItem(id: string) {
  const config: any = { containsPHI: false }
  return api.delete(`/api/treatmenthistories/${id}`, config)
    .then(response => {});
}

function useDeleteTreatmentHistoryItem(options?: UseMutationOptions<void, AxiosError, string>) {
  return useMutation(
    async id => deleteTreatmentHistoryItem(id),
    options,
  );
}

export { getTreatmentHistory, useGetTreatmentHistory, useCreateTreatmentHistoryItem, useCreateTreatmentHistoryItems, createTreatmentHistory, useUpdateTreatmentHistoryItem, useDeleteTreatmentHistoryItem, useUpdateTreatmentHistoriesSortOrder };

